<template>
  <ModalDialog
    ref="dialog"
    :title="`${contactName} - Price List`"
    icon="fa-tag"
    sizeClass="modal-xl"
    :showSubmit="false"
    @closed="onClosed"
    :scrollable="true"
    >

    <NotificationBar v-if="loadingError">{{loadingError}}</NotificationBar>
    <Spinner v-if="loading" />
    <NotificationBar v-if="!loading && linkedDataSourceRequestGroup" type="info">
      <strong>Note:</strong> This price list is linked to another pending price request to provide pending sales prices
    </NotificationBar>
    <TabbedBox v-if="!loading">
      <template v-slot:tablist>
        <Tab :first="true" tabid="tab-pricelistpreview-pricing-processed">
          <i class="far fa-cogs"></i> Processed Glass
        </Tab>
        <Tab tabid="tab-pricelistpreview-pricing-services">
          <i class="far fa-tools"></i> Services
        </Tab>
        <Tab tabid="tab-pricelistpreview-pricing-general">
          <i class="far fa-box-up"></i> General
        </Tab>
        <Tab tabid="tab-pricelistpreview-pricing-config">
          <i class="far fa-cogs"></i> Price Configuration
        </Tab>
      </template>

      <template v-slot:tabcontent>
        <!-- Processed Stock Pricing -->
        <TabPage :first="true" tabid="tab-pricelistpreview-pricing-processed">
          <div class="row">
            <div class="col-lg-12">
              <h3>Sales Rates</h3>
            </div>
          </div>

          <div class="row">
            <div class="col-lg-12">
              <div class="table-responsive table-hover">
                <table class="table collapsible-list" style="width:100%">
                  <thead>
                    <tr>
                      <th width="25%">Category</th>
                      <template v-for="(priceGroup, index) in processedPriceGroupList">
                        <th :key="index">{{priceGroup.name}}</th>
                      </template>
                      <th width="150px" v-if="isPricelistEditable">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="(stockCategory, index) in priceListRootData.glass">
                      <tr :key="index" class="collapsible-list-row-header">
                        <td class="font-weight-bold" @click="categoryBarClicked($event, stockCategory, true)">
                          <span v-html="stockCategory.displayName"></span>
                        </td>
                        <template v-for="(priceGroup, priceGroupIndex) in processedPriceGroupList">
                          <td :key="priceGroupIndex" @click="categoryBarClicked($event, stockCategory, false)">
                            <span v-if="!stockCategory.editing">
                              <span :class="[processedCategoryRateContainsRateChange(stockCategory, priceGroup.id) ? 'pricing-rate-changed font-weight-bold' : '']">
                                {{getProcessedCategoryRate(stockCategory, priceGroup.id).name}}
                              </span>
                              <span v-if="processedCategoryRateContainsRateVariation(stockCategory, priceGroup.id)" class="pricing-rate-changed">
                                &nbsp;<Tooltip label="Contains Rate Variations" placement="top"><i class="far fa-exclamation-circle"></i></Tooltip>
                              </span>
                              <span v-if="processedCategoryRateContainsVariation(stockCategory, priceGroup.id)" class="pricing-list-variation">
                                &nbsp;<Tooltip label="Contains Price Variations" placement="top"><i class="far fa-usd-circle"></i></Tooltip>
                              </span>
                              <span v-if="getCategoryStockListContainsPending(stockCategory, priceGroup.id)" class="pricing-pending-update">
                                &nbsp;<Tooltip label="Contains Pending Sales Prices" placement="top"><i class="far fa-clock"></i></Tooltip>
                              </span>
                              <span v-if="processedCategoryRateContainsRateChange(stockCategory, priceGroup.id)" class="pricing-rate-changed">
                                &nbsp;<Tooltip :label="`Changed from ${getProcessedCategoryRate(stockCategory, priceGroup.id).prevRateName}`" placement="top"><i class="far fa-exclamation-circle"></i></Tooltip>
                              </span>
                            </span>
                            <span v-show="stockCategory.editing">
                              <FormSelect
                                :options="salesRateOptionList"
                                :value="getProcessedCategoryRate(stockCategory, priceGroup.id).id"
                                @input="setProcessedCategoryRate(stockCategory, priceGroup.id, $event)"
                              />
                            </span>
                          </td>
                        </template>
                        <td v-if="isPricelistEditable">
                          <ActionDropMenu :tableAction="true" v-if="!stockCategory.editing && !isEditing">
                            <DropdownMenuItem @click="setEditingStockCategoryPriceData(stockCategory)" icon="fa-pencil" label="Amend">Amend</DropdownMenuItem>
                          </ActionDropMenu>
                          <div v-if="stockCategory.editing">
                            <FormButton class="btn-xxs btn-white mr-2" @click="cancelEditingStockCategoryPriceData(stockCategory)">Cancel</FormButton>
                            <FormButton class="btn-xxs btn-primary" @click="submitEditingStockCategoryPriceData(stockCategory, StockPriceTypes.processedPriceGroup)" :loading="stockCategory.submitting" loadingStyle="slide-right">Update</FormButton>
                          </div>
                        </td>
                      </tr>
                      <tr :key="`processed-empty-category-${index}`" v-show="stockCategory.expanded && isCategoryEmpty(stockCategory)">
                        <td :colspan="processedPriceGroupList.length + 2" class="text-muted font-italic text-center">
                          This category contains no stock
                        </td>
                      </tr>
                      <template v-for="stock in stockCategory.stockList">
                        <tr :key="`${stock.id}-stock`" class="" v-show="stockCategory.expanded">
                          <td>
                            <span class="ml-3" /><LinkStock :id="stock.id" :name="stock.name" />
                          </td>
                          <template v-for="(priceGroup, priceGroupIndex) in processedPriceGroupList">
                            <td :key="`${priceGroupIndex}-stock-price`">

                              <PriceListDataCell
                                :stock="stock"
                                :priceGroupId="priceGroup.id"
                                :editing="stock.editing"
                                :salesRateList="salesRateList"
                                @displaySalesRatePopover="displaySalesRatePopover($event, $event.stock, $event.priceGroupId)"
                                @displayPriceVariationPopover="displayPriceVariationPopover($event, $event.stock, $event.priceGroupId)"
                              />
                            </td>
                          </template>
                          <td v-if="isPricelistEditable">
                            <ActionDropMenu :tableAction="true" v-if="!stock.editing && !isEditing">
                              <DropdownMenuItem @click="setEditingStockPriceData(stock)" icon="fa-pencil" label="Amend">Amend</DropdownMenuItem>
                            </ActionDropMenu>
                            <div v-if="stock.editing">
                              <FormButton class="btn-xxs btn-white mr-2" @click="cancelEditingStockPriceData(stock)">Cancel</FormButton>
                              <FormButton class="btn-xxs btn-primary" @click="submitEditingStockPriceData(stock)" :loading="stock.submitting" loadingStyle="slide-right">Update</FormButton>
                            </div>
                          </td>
                        </tr>
                      </template>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </TabPage>

        <!-- Services Pricing -->
        <TabPage tabid="tab-pricelistpreview-pricing-services">
          <div class="row">
            <div class="col-lg-12">

              <ul class="nav nav-tabs" role="tablist">
                <Tab v-for="(priceGroup, index) in servicesPriceGroupList" :first="index == 0" :tabid="`tab-preview-services-pricegroup-${index}`" :key="index">
                  {{priceGroup.name}}
                </Tab>
              </ul>

              <div class="tab-content">
                <TabPage v-for="(priceGroup, index) in servicesPriceGroupList" :first="index == 0" :tabid="`tab-preview-services-pricegroup-${index}`" :key="index">
                  <div class="row">
                    <div class="col-lg-12">
                      <h3>Sales Rates</h3>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col-lg-12">
                      <div class="table-responsive table-hover">
                        <table class="table collapsible-list" style="width:100%">
                          <thead>
                            <tr>
                              <th width="25%">Category</th>
                              <template v-for="(serviceRange, serviceIndex) in priceGroup.servicePriceRanges">
                                <th :key="serviceIndex">{{serviceRange.name}}</th>
                              </template>
                              <th width="150px" v-if="isPricelistEditable">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            <template v-for="(stockCategory, categoryIndex) in priceListRootData.glassServices">
                              <tr :key="categoryIndex" class="collapsible-list-row-header">
                                <td class="font-weight-bold" @click="categoryBarClicked($event, stockCategory, true)">
                                  <span v-html="stockCategory.displayName"></span>
                                </td>
                                <template v-for="(serviceRange, serviceIndex) in priceGroup.servicePriceRanges">
                                  <td :key="serviceIndex" @click="categoryBarClicked($event, stockCategory, false)">
                                    <span v-if="!stockCategory.editing">
                                      <span :class="[serviceRangeCategoryRateContainsRateChange(stockCategory, priceGroup.id, serviceRange.id) ? 'pricing-rate-changed font-weight-bold' : '']">
                                        {{getServiceRangeCategoryRate(stockCategory, priceGroup.id, serviceRange.id).name}}
                                      </span>
                                      <span v-if="serviceRangeCategoryRateContainsRateVariation(stockCategory, priceGroup.id, serviceRange.id)" class="pricing-rate-changed">
                                        &nbsp;<Tooltip label="Contains Rate Variations" placement="top"><i class="far fa-exclamation-circle"></i></Tooltip>
                                      </span>
                                      <span v-if="serviceRangeCategoryRateContainsVariation(stockCategory, priceGroup.id, serviceRange.id)" class="pricing-list-variation">
                                        &nbsp;<Tooltip label="Contains Price Variations" placement="top"><i class="far fa-usd-circle"></i></Tooltip>
                                      </span>
                                      <span v-if="getCategoryStockListContainsPending(stockCategory, priceGroup.id, serviceRange.id)" class="pricing-pending-update">
                                        &nbsp;<Tooltip label="Contains Pending Sales Prices" placement="top"><i class="far fa-clock"></i></Tooltip>
                                      </span>
                                      <span v-if="serviceRangeCategoryRateContainsRateChange(stockCategory, priceGroup.id, serviceRange.id)" class="pricing-rate-changed">
                                        &nbsp;<Tooltip :label="`Changed from ${getServiceRangeCategoryRate(stockCategory, priceGroup.id, serviceRange.id).prevRateName}`" placement="top"><i class="far fa-exclamation-circle"></i></Tooltip>
                                      </span>
                                    </span>
                                    <span v-show="stockCategory.editing">
                                      <FormSelect
                                        :options="salesRateOptionList"
                                        :value="getServiceRangeCategoryRate(stockCategory, priceGroup.id, serviceRange.id).id"
                                        @input="setServiceRangeCategoryRate(stockCategory, priceGroup.id, serviceRange.id, $event)"
                                      />
                                    </span>
                                  </td>
                                </template>
                                <td v-if="isPricelistEditable">
                                  <ActionDropMenu :tableAction="true" v-if="!stockCategory.editing && !isEditing">
                                    <DropdownMenuItem @click="setEditingStockCategoryPriceData(stockCategory)" icon="fa-pencil" label="Amend">Amend</DropdownMenuItem>
                                  </ActionDropMenu>
                                  <div v-if="stockCategory.editing">
                                    <FormButton class="btn-xxs btn-white mr-2" @click="cancelEditingStockCategoryPriceData(stockCategory)">Cancel</FormButton>
                                    <FormButton class="btn-xxs btn-primary" @click="submitEditingStockCategoryPriceData(stockCategory, StockPriceTypes.servicePriceRange, priceGroup.id)" :loading="stockCategory.submitting" loadingStyle="slide-right">Update</FormButton>
                                  </div>
                                </td>
                              </tr>
                              <tr :key="`service-empty-category-${categoryIndex}`" v-show="stockCategory.expanded && isCategoryEmpty(stockCategory)">
                                <td :colspan="priceGroup.servicePriceRanges.length + 2" class="text-muted font-italic text-center">
                                  This category contains no stock
                                </td>
                              </tr>
                              <template v-for="(stock, stockIndex) in stockCategory.stockList">
                                <tr :key="`pg${priceGroup.id}-${stockIndex}-${categoryIndex}-stock`" class="" v-show="stockCategory.expanded">
                                  <td>
                                    <span class="ml-3" /><LinkStock :id="stock.id" :name="stock.name" />
                                  </td>
                                  <template v-for="(serviceRange, serviceIndex) in priceGroup.servicePriceRanges">
                                    <td :key="serviceIndex">
                                      <PriceListDataCell
                                        :stock="stock"
                                        :priceGroupId="priceGroup.id"
                                        :serviceRangeId="serviceRange.id"
                                        :editing="stock.editing"
                                        :salesRateList="salesRateList"
                                        @displaySalesRatePopover="displaySalesRatePopover($event, $event.stock, $event.priceGroupId, $event.serviceRangeId)"
                                        @displayPriceVariationPopover="displayPriceVariationPopover($event, $event.stock, $event.priceGroupId, $event.serviceRangeId)"
                                      />
                                    </td>
                                  </template>
                                  <td v-if="isPricelistEditable">
                                    <ActionDropMenu :tableAction="true" v-if="!stock.editing && !isEditing">
                                      <DropdownMenuItem @click="setEditingStockPriceData(stock)" icon="fa-pencil" label="Amend">Amend</DropdownMenuItem>
                                    </ActionDropMenu>
                                    <div v-if="stock.editing">
                                      <FormButton class="btn-xxs btn-white mr-2" @click="cancelEditingStockPriceData(stock)">Cancel</FormButton>
                                      <FormButton class="btn-xxs btn-primary" @click="submitEditingStockPriceData(stock)" :loading="stock.submitting" loadingStyle="slide-right">Update</FormButton>
                                    </div>
                                  </td>
                                </tr>
                              </template>
                            </template>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                </TabPage>
              </div>
            </div>
          </div>
        </TabPage>

        <!-- General Stock Pricing -->
        <TabPage tabid="tab-pricelistpreview-pricing-general">
          <div class="row">
            <div class="col-lg-12">
              <h3>Sales Rates</h3>
            </div>
          </div>

          <div class="row">
            <div class="col-lg-12">
              <div class="table-responsive table-hover">
                <table class="table collapsible-list" style="width:100%">
                  <thead>
                    <tr>
                      <th width="25%">Category</th>
                      <th>Price</th>
                      <th v-if="isPricelistEditable">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="(stockCategory, index) in priceListRootData.general">
                      <tr :key="index" class="collapsible-list-row-header">
                        <td class="font-weight-bold" @click="categoryBarClicked($event, stockCategory, true)">
                          <span v-html="stockCategory.displayName"></span>
                        </td>
                        <td @click="categoryBarClicked($event, stockCategory, false)">
                          <span v-if="!stockCategory.editing">
                            <span :class="[generalCategoryRateContainsRateChange(stockCategory) ? 'pricing-rate-changed font-weight-bold' : '']">
                              {{getGeneralCategoryRate(stockCategory).name}}
                            </span>
                            <span v-if="generalCategoryRateContainsRateVariation(stockCategory)" class="pricing-rate-changed">
                              &nbsp;<Tooltip label="Contains Rate Variations" placement="top"><i class="far fa-exclamation-circle"></i></Tooltip>
                            </span>
                            <span v-if="generalCategoryRateContainsVariation(stockCategory)" class="pricing-list-variation">
                              &nbsp;<Tooltip label="Contains Price Variations" placement="top"><i class="far fa-usd-circle"></i></Tooltip>
                            </span>
                            <span v-if="getCategoryStockListContainsPending(stockCategory)" class="pricing-pending-update">
                              &nbsp;<Tooltip label="Contains Pending Sales Prices" placement="top"><i class="far fa-clock"></i></Tooltip>
                            </span>
                            <span v-if="generalCategoryRateContainsRateChange(stockCategory)" class="pricing-rate-changed">
                              &nbsp;<Tooltip :label="`Changed from ${getGeneralCategoryRate(stockCategory).prevRateName}`" placement="top"><i class="far fa-exclamation-circle"></i></Tooltip>
                            </span>
                          </span>
                          <span v-show="stockCategory.editing">
                            <FormSelect
                              :options="salesRateOptionList"
                              :value="getGeneralCategoryRate(stockCategory).id"
                              @input="setGeneralCategoryRate(stockCategory, $event)"
                            />
                          </span>
                        </td>
                        <td v-if="isPricelistEditable">
                          <ActionDropMenu :tableAction="true" v-if="!stockCategory.editing && !isEditing">
                            <DropdownMenuItem @click="setEditingStockCategoryPriceData(stockCategory)" icon="fa-pencil" label="Amend">Amend</DropdownMenuItem>
                          </ActionDropMenu>
                          <div v-if="stockCategory.editing">
                            <FormButton class="btn-xxs btn-white mr-2" @click="cancelEditingStockCategoryPriceData(stockCategory)">Cancel</FormButton>
                            <FormButton class="btn-xxs btn-primary" @click="submitEditingStockCategoryPriceData(stockCategory, StockPriceTypes.general)" :loading="stockCategory.submitting" loadingStyle="slide-right">Update</FormButton>
                          </div>
                        </td>
                      </tr>
                      <tr :key="`general-empty-category-${index}`" v-show="stockCategory.expanded && isCategoryEmpty(stockCategory)">
                        <td :colspan="3" class="text-muted font-italic text-center">
                          This category contains no stock
                        </td>
                      </tr>
                      <template v-for="(stock, stockIndex) in stockCategory.stockList">
                        <tr :key="`${stockIndex}-${index}-stock`" class="" v-show="stockCategory.expanded">
                          <td>
                            <span class="ml-3" /><LinkStock :id="stock.id" :name="stock.name" />
                          </td>
                          <td>
                            <PriceListDataCell
                              :stock="stock"
                              :editing="stock.editing"
                              :salesRateList="salesRateList"
                              @displaySalesRatePopover="displaySalesRatePopover($event, $event.stock)"
                              @displayPriceVariationPopover="displayPriceVariationPopover($event, $event.stock)"
                            />
                          </td>
                          <td v-if="isPricelistEditable">
                            <ActionDropMenu :tableAction="true" v-if="!stock.editing && !isEditing">
                              <DropdownMenuItem @click="setEditingStockPriceData(stock)" icon="fa-pencil" label="Amend">Amend</DropdownMenuItem>
                            </ActionDropMenu>
                            <div v-if="stock.editing">
                              <FormButton class="btn-xxs btn-white mr-2" @click="cancelEditingStockPriceData(stock)">Cancel</FormButton>
                              <FormButton class="btn-xxs btn-primary" @click="submitEditingStockPriceData(stock)" :loading="stock.submitting" loadingStyle="slide-right">Update</FormButton>
                            </div>
                          </td>
                        </tr>
                      </template>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </TabPage>

        <TabPage tabid="tab-pricelistpreview-pricing-config">

          <FormGroup :form="priceGroupOverrideForm">
            <CustomerStockPriceGroupList v-model="customerPriceList.priceData.priceGroupOverrideSettings" :editing="editingPriceGroupOverrides">
              <template v-slot:title>
                <ActionDropMenu v-if="isPricelistEditable && !isEditing" class="float-right">
                  <DropdownMenuItem icon="fa-pencil" @click="editPriceGroupSettings">Edit Price Config</DropdownMenuItem>
                </ActionDropMenu>
                <ContentTitle>Customer Price Configuration</ContentTitle>
                <ContentSubTitle>Overrides for the global price group settings</ContentSubTitle>
              </template>
            </CustomerStockPriceGroupList>

            <div v-if="editingPriceGroupOverrides">
              <FormHorizontalRule />
              <FormSubmit label="Update" :submitting="priceGroupOverrideSubmitting" @cancelled="cancelPriceGroupOverrideEditing" @submitted="submitPriceGroupOverrideEditing"></FormSubmit>
            </div>
          </FormGroup>
        </TabPage>

      </template>
    </TabbedBox>
    
    <StockPriceListRatePopover
      ref="salesRatePopover"
      :pricingSalesRateList="salesRateList"
      @applyStockRate="applySalesRateVariationByStockId($event.stockId, $event.salesRateId, $event.stockPriceGroupId, $event.serviceRangeId)"
      @removeStockRateVariation="removeSalesRateVariationByStockId($event.stockId, $event.stockPriceGroupId, $event.serviceRangeId)">
    </StockPriceListRatePopover>

    <StockPriceListPricePopover
      ref="priceVariationPopover"
      :pricingSalesRateList="salesRateList"
      @applyPriceVariation="updateStockPriceWithVariationByStockId($event.stockId, $event.price, $event.stockPriceGroupId, $event.serviceRangeId)"
      @removePriceVariation="removeStockPriceVariationByStockId($event.stockId, $event.stockPriceGroupId, $event.serviceRangeId)">
    </StockPriceListPricePopover>
  </ModalDialog>
</template>

<script>
import _ from 'lodash'
import { round } from 'mathjs'

import FormMixin from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import ModalDialogMixin from '@/mixins/modalDialog'

import CustomerStockPriceGroupList from '@/modules/pricing/components/customerStockPriceGroupList'
import PriceListDataCell from '@/modules/pricing/components/priceListDataCell'
import StockPriceListRatePopover from '@/modules/pricing/components/stockPriceListRatePopover'
import StockPriceListPricePopover from '@/modules/pricing/components/stockPriceListPricePopover'

import CustomerPriceListMixin from '@/modules/pricing/mixins/customerPriceListMixin.js'
import { StockPriceTypes, InventoryStockTypes, CustomerOverrideTypes, CustomerOverrideLevelTypes } from '@/store/types'

export default {
  name: 'CustomerPriceListPreviewDialog',
  mixins: [FormMixin, ModalDialogMixin, CustomerPriceListMixin],
  components: {
    ModalDialog,
    PriceListDataCell,
    StockPriceListRatePopover,
    StockPriceListPricePopover,
    CustomerStockPriceGroupList
  },
  props: {
    contactId: Number,
    contactName: String,
    priceListId: Number
  },
  data() {
    return {
      salesRateOptionList: [],

      loading: false,
      isEditing: false,

      priceGroupOverrideForm: {},
      editingPriceGroupOverrides: false,
      priceGroupOverrideSubmitting: false,
    }
  },
  computed: {
    isPricelistEditable() {
      if(!this.$data.customerPriceList || !this.$data.customerPriceList.priceList)
        return false
      if(!this.userHasUpdatePermission(this.PermissionTypes.PricingPermissionTypes.pricingApproval))
        return false
      if(this.$data.customerPriceList.priceList.isApproved || this.$data.customerPriceList.priceList.requestStatusVerified)
        return false
      return true
    },
    linkedDataSourceRequestGroup() {
      if(!this.$data.customerPriceList.priceList)
        return false
      return this.$data.customerPriceList.priceList.sourceLinkRequestGroup
    },
  },
  created() {
    this.StockPriceTypes = StockPriceTypes
  },
  mounted() {
  },
  methods: {
    reloadData() {
      this.requestData()
    },
    requestData() {
      this.loading = true
      this.requestCustomerPriceListData(this.contactId, this.priceListId)
      .then(() => {
        // Note: Data stored via CustomerPriceListMixin

        this.$data.salesRateOptionList = this.$data.salesRateList.map(sales => {
          return {
            name: sales.name,
            value: sales.id
          }
        })
      })
      .finally(() => {
        this.loading = false
      })
    },
    show() {
      this.requestData()
      return ModalDialogMixin.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(false)
    },

    setEditingStockCategoryPriceData(stockCategory) {
      stockCategory.editing = true
      stockCategory.submitting = true
      this.isEditing = true

      // Store current sales rates as original for restoring
      if(stockCategory.stockType == InventoryStockTypes.glass) {
        
        this.processedPriceGroupList.forEach(priceGroup => {
          var customerPrice = this.customerPriceList.priceData.priceSettings.find(price => {
            return price.priceType == StockPriceTypes.processedPriceGroup &&
            price.stockCategoryId == stockCategory.id &&
            price.stockPriceGroupId == priceGroup.id &&
            price.overrideType == CustomerOverrideTypes.rate &&
            price.overrideLevelType == CustomerOverrideLevelTypes.category
          })
          
          if(customerPrice)
            customerPrice.originalSalesRateId = customerPrice.salesRateId
        })
      }
      if(stockCategory.stockType == InventoryStockTypes.glassService) {

        // Restore for ALL price groups
        this.servicesPriceGroupList.forEach(priceGroup => {
          priceGroup.servicePriceRanges.forEach(serviceRange => {
            var customerPrice = this.customerPriceList.priceData.priceSettings.find(price => {
              return price.priceType == StockPriceTypes.servicePriceRange &&
              price.stockCategoryId == stockCategory.id &&
              price.stockPriceGroupId == priceGroup.id &&
              price.servicePriceRangeId == serviceRange.id &&
              price.overrideType == CustomerOverrideTypes.rate &&
              price.overrideLevelType == CustomerOverrideLevelTypes.category
            })
            
            if(customerPrice)
              customerPrice.originalSalesRateId = customerPrice.salesRateId
          })
        })
      }
      if(stockCategory.stockType == InventoryStockTypes.general) {
        var customerPrice = this.customerPriceList.priceData.priceSettings.find(price => {
          return price.priceType == StockPriceTypes.general &&
          price.stockCategoryId == stockCategory.id &&
          price.overrideType == CustomerOverrideTypes.rate &&
          price.overrideLevelType == CustomerOverrideLevelTypes.category
        })

        if(customerPrice)
          customerPrice.originalSalesRateId = customerPrice.salesRateId
      }

    },

    setEditingStockPriceData(stock) {
      stock.editing = true
      stock.submitting = false
      this.isEditing = true

      // Store the current data for restoring
      if(stock.stockType == InventoryStockTypes.glass || stock.stockType == InventoryStockTypes.glassService) {
        stock.originalPriceGroups = _.cloneDeep(stock.priceGroups)
      }
      if(stock.stockType == InventoryStockTypes.general) {
        stock.originalPriceData = _.cloneDeep(stock.priceData)
      }
    },

    cancelEditingStockCategoryPriceData(stockCategory) {
      this.isEditing = false
      stockCategory.editing = false
      stockCategory.submitting = false

      // For now just reload data to restore. Might be too slow for future usage
      this.reloadData()

      // JB: Disabled while just reloading data
      //this.restoreStockCategoryRate(stockCategory)
    },
    submitEditingStockCategoryPriceData(stockCategory, stockPriceType, priceGroupId = null) {
      var updateListData = this.buildServerPriceListUpdateListData({
        updateStock: true,      // Have to update all stock under a category as it may have changed during category updates (eg, variation clearing)
        filterCategoryId: stockCategory.id
      })

      let updateData = {
        priceData: updateListData,
        filterStockCategoryId: stockCategory.id,
        filterPriceType: stockPriceType,
        updateCategory: true,
        updateStock: true      // Server will clear out all stock variations not sent through
      }

      stockCategory.submitting = true

      this.$apiService.pricingService.updateCustomerPriceList(this.$props.contactId, this.$props.priceListId, updateData)
      .then(response => {
        this.successToast('Prices amended')
      })
      .catch(err => {
        this.displayNetworkError(err)
      })
      .finally(() => {
        this.isEditing = false
        stockCategory.editing = false
        stockCategory.submitting = false
      })
    },

    cancelEditingStockPriceData(stock) {
      this.isEditing = false
      stock.editing = false
      stock.submitting = false

      // For now just reload data to restore. Might be too slow for future usage
      this.reloadData()

      // Restore original data / reload all price list
      // JB: Disabled while just reloading data
      /*
      if(stock.stockType == InventoryStockTypes.glass) {
        this.$data.stockPriceGroupList.forEach(priceGroup => {
          if(stock.priceGroups[priceGroup.id]) {
            var priceData = stock.priceGroups[priceGroup.id]
            let origData = stock.originalPriceGroups[priceGroup.id]

            priceData.isVariation = origData.isVariation
            priceData.isRateVariation = origData.isRateVariation
            priceData.price = origData.price
            priceData.calculatedPrice = origData.calculatedPrice
            priceData.variationPrice = origData.variationPrice
            priceData.rateVariationSalesRateId = origData.rateVariationSalesRateId
          }
        })
      }
      if(stock.stockType == InventoryStockTypes.glassService) {
        this.$data.stockPriceGroupList.forEach(priceGroup => {
          priceGroup.servicePriceRanges.forEach(serviceRange => {

            if(stock.priceGroups[priceGroup.id][serviceRange.id] && stock.originalPriceGroups[priceGroup.id][serviceRange.id]) {
              var priceData = stock.priceGroups[priceGroup.id][serviceRange.id]
              let origData = stock.originalPriceGroups[priceGroup.id][serviceRange.id]

              priceData.isVariation = origData.isVariation
              priceData.isRateVariation = origData.isRateVariation
              priceData.price = origData.price
              priceData.calculatedPrice = origData.calculatedPrice
              priceData.variationPrice = origData.variationPrice
              priceData.rateVariationSalesRateId = origData.rateVariationSalesRateId
            }
          })
        })
      }
      if(stock.stockType == InventoryStockTypes.general) {
        if(stock.priceData) {
          var priceData = stock.priceData
          let origData = stock.originalPriceData

          priceData.isVariation = origData.isVariation
          priceData.isRateVariation = origData.isRateVariation
          priceData.price = origData.price
          priceData.calculatedPrice = origData.calculatedPrice
          priceData.variationPrice = origData.variationPrice
          priceData.rateVariationSalesRateId = origData.rateVariationSalesRateId
        }
      }*/
    },

    submitEditingStockPriceData(stock) {

      var updateListData = this.buildServerPriceListUpdateListData({
        updateCategory: false,
        filterStockId: stock.id
      })

      stock.submitting = true
      let updateData = {
        priceData: updateListData,
        filterStockId: stock.id,
        updateCategory: false,
        updateStock: true
      }

      this.$apiService.pricingService.updateCustomerPriceList(this.$props.contactId, this.$props.priceListId, updateData)
      .then(response => {
        this.successToast('Prices amended')


      })
      .catch(err => {
        this.displayNetworkError(err)
      })
      .finally(() => {
        stock.submitting = false
        stock.editing = false
        this.isEditing = false
      })
    },

    displaySalesRatePopover(evt, stock, stockPriceGroupId = null, serviceRangeId = null) {
      var priceData = stock.priceData
      if(stockPriceGroupId)
        priceData = stock.priceGroups[stockPriceGroupId]
      if(stockPriceGroupId && serviceRangeId)
        priceData = stock.priceGroups[stockPriceGroupId][serviceRangeId]

      var selectedSalesRateId = 0
      if(priceData.isRateVariation)
        selectedSalesRateId = priceData.salesRateId

      this.$refs.salesRatePopover.displayPriceSelectionFromElement(evt.target, stock.id, stockPriceGroupId, serviceRangeId, selectedSalesRateId, priceData.salesPrice, priceData.isRateVariation)
    },
    displayPriceVariationPopover(evt, stock, stockPriceGroupId = null, serviceRangeId = null) {
      var priceData = stock.priceData
      if(stockPriceGroupId)
        priceData = stock.priceGroups[stockPriceGroupId]
      if(stockPriceGroupId && serviceRangeId)
        priceData = stock.priceGroups[stockPriceGroupId][serviceRangeId]

      this.$refs.priceVariationPopover.displayPriceSelectionFromElement(evt.target, stock.id, stockPriceGroupId, serviceRangeId, priceData.price, priceData.salesPrice, priceData.isVariation)
    },

    editPriceGroupSettings() {
      this.isEditing = true
      this.editingPriceGroupOverrides = true
    },
    cancelPriceGroupOverrideEditing() {
      this.isEditing = false
      this.editingPriceGroupOverrides = false

      this.reloadData()
    },
    submitPriceGroupOverrideEditing() {
      this.priceGroupOverrideForm = this.clearFormResponse()
      this.priceGroupOverrideSubmitting = true
      
      let updateData = {
        priceData: [],
        updateCategory: false,
        updateStock: false,
        priceGroupOverrideSettings: this.customerPriceList.priceData.priceGroupOverrideSettings || []
      }

      this.$apiService.pricingService.updateCustomerPriceList(this.$props.contactId, this.$props.priceListId, updateData)
      .then(response => {
        this.successToast('Price Group Config amended')
      })
      .catch(err => {
        this.priceGroupOverrideForm = this.processFormResponse(err)
      })
      .finally(() => {
        this.priceGroupOverrideSubmitting = false
        this.editingPriceGroupOverrides = false
        this.isEditing = false
      })

    }
  }  
}
</script>